import { z } from "zod";
import { ANSWERED_OUTCOMES, CALL_OUTCOMES, CALL_RECORDING_STATES, callViewSchema } from "./calls.js";
import { emailEventSchema } from "./email-events.js";
import { isoDaySchema } from "./filters.js";
import { freeTextSchema } from "./leads.js";
import { actorViewSchema, personSchema } from "./people.js";
import { idSchema } from "./text.js";
import { timelineRecordingSchema } from "./timeline.js";
import { leadWhatsAppSendViaSchema, whatsAppAutomaticMessageSchema, whatsAppChannelSchema, whatsAppContentKindSchema, whatsAppMessageStatusSchema, whatsAppTemplateKeySchema, } from "./whatsapp.js";
// «Conversazioni» (piano M4, Task 18, D14; `PRODUCT_SPEC.md` §6, «Vista Conversazioni»): `GET /api/conversations/calls`, le
// telefonate nel perimetro dell'area `conversations` di chi guarda, poi il periodo, i filtri, la ricerca nel testo del Lead,
// dalla più recente, a pagine con un cursore. Nessuna copia: ogni riga è la telefonata di `calls`, la stessa della Timeline del
// Lead, con i suoi appunti e lo stato della registrazione detto con la regola della Timeline (`recordingDisplayState` del
// dominio). L'audio si chiede con `GET /api/calls/:callId/recording`, con i suoi permessi; un appunto si aggiunge con
// `POST /api/leads/:leadId/notes` e `callId`, e compare anche nella Timeline. M6 aggiunge `GET /api/conversations/whatsapp` con
// lo stesso perimetro (in fondo al file).
/**
 * I periodi, giorni di calendario interi nel fuso dell'azienda con gli estremi compresi (decisione del 28 settembre 2026):
 * «Oggi», «Ieri», «Ultimi 7 giorni» (oggi più i 6 giorni prima), «Ultimi 30 giorni», «Tutte» (nessun limite) e «Personalizzato»
 * (`from` e `to`, entrambi compresi).
 */
export const CONVERSATION_PERIODS = ["today", "yesterday", "last7", "last30", "all", "custom"];
/**
 * Le voci di «Esito», con la regola della questione 202: il primo passo (`calls.outcome`) vale per ogni chiamata con quel passo,
 * anche con il secondo («Risposta» comprende «Richiamare»); il secondo passo (`calls.answered_outcome`) soltanto per sé.
 */
export const CONVERSATION_OUTCOMES = [...CALL_OUTCOMES, ...ANSWERED_OUTCOMES];
/**
 * Le voci di «Registrazione»: i cinque stati da mostrare e `none`, «Senza risposta: niente da registrare» (lo stato da mostrare
 * vuoto: una chiamata senza risposta e senza riga della registrazione).
 */
export const CONVERSATION_RECORDING_FILTERS = [...CALL_RECORDING_STATES, "none"];
/** La lunghezza massima del cursore: il server non ne emette mai uno più lungo. */
export const CONVERSATIONS_CURSOR_MAX_LENGTH = 200;
/**
 * Query di `GET /api/conversations/calls`. Un parametro sconosciuto, un valore fuori dai limiti o un cursore che non viene da
 * una pagina precedente è `422 validation_failed` con il nome del parametro. `from` e `to` (`AAAA-MM-GG`) ci sono soltanto con
 * `period=custom`, tutti e due, e `from` non dopo `to`. `who` (l'autore, «Di chi») vale soltanto per chi vede le chiamate di
 * tutti (`canFilterByPerson`): per gli altri non allarga e non restringe nulla. `ownerAtCall` è l'owner del Lead al momento della
 * chiamata («Owner allora»). `audioOnly` («Solo audio ascoltabile») è `true` o `false`, alla lettera. La ricerca segue la regola
 * dei testi liberi, come quella dell'elenco dei Lead.
 */
export const conversationsQuerySchema = z
    .strictObject({
    period: z.enum(CONVERSATION_PERIODS).default("last7"),
    from: isoDaySchema.optional(),
    to: isoDaySchema.optional(),
    who: idSchema.optional(),
    direction: z.enum(["outbound", "inbound"]).optional(),
    outcome: z.enum(CONVERSATION_OUTCOMES).optional(),
    ownerAtCall: idSchema.optional(),
    recording: z.enum(CONVERSATION_RECORDING_FILTERS).optional(),
    audioOnly: z.stringbool({ truthy: ["true"], falsy: ["false"], case: "sensitive" }).optional(),
    q: freeTextSchema(200).optional(),
    cursor: z.string().max(CONVERSATIONS_CURSOR_MAX_LENGTH).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
})
    .superRefine(checkPeriod);
/**
 * `from` e `to` soltanto con `period=custom`, tutti e due, e `from` non dopo `to`: la regola delle due schede, che vale anche per
 * il periodo delle Opportunità (`opportunities.ts`).
 */
export function checkPeriod(query, context) {
    if (query.period !== "custom") {
        if (query.from !== undefined)
            context.addIssue({ code: "custom", path: ["from"], message: "from is only for period=custom" });
        if (query.to !== undefined)
            context.addIssue({ code: "custom", path: ["to"], message: "to is only for period=custom" });
        return;
    }
    if (query.from === undefined)
        context.addIssue({ code: "custom", path: ["from"], message: "period=custom needs from" });
    if (query.to === undefined)
        context.addIssue({ code: "custom", path: ["to"], message: "period=custom needs to" });
    // Due giorni `AAAA-MM-GG` si confrontano come testi.
    if (query.from !== undefined && query.to !== undefined && query.from > query.to)
        context.addIssue({ code: "custom", path: ["to"], message: "to is before from" });
}
/** Il Lead della telefonata: nome composto e salone come nell'elenco, e il suo owner di adesso («oggi: …»). */
export const conversationLeadSchema = z.object({
    id: z.string(),
    displayName: z.string().nullable(),
    salonName: z.string().nullable(),
    currentOwner: personSchema.nullable(),
});
/** Un appunto della telefonata, come nella Timeline: chi l'ha scritto (il nome di allora) e quando. */
export const conversationNoteSchema = z.object({ id: z.string(), body: z.string(), occurredAt: z.string(), actor: actorViewSchema });
/**
 * Una riga: la telefonata come la vede chi guarda (`callViewSchema`: direzione, inizio, stato, esito in due passi, durata, autore
 * e se chi guarda ne deve dare l'esito), il suo Lead (`null` soltanto per una chiamata senza Lead, che oggi non c'è), l'owner del
 * Lead al momento della chiamata (`null` per le chiamate di Close, che non lo sanno) e se oggi l'owner è un'altra persona
 * (`ownerChanged`, per «oggi: …»: le appartenenze di allora e di adesso, mai i nomi, che cambiano e si ripetono; vero anche
 * quando allora c'era un owner e adesso il Lead non ne ha, o quando l'owner di allora non è noto e adesso c'è), la registrazione come la dice la Timeline
 * (lo stato da mostrare, la durata dell'audio e la scadenza; `null` quando non c'è niente da registrare), gli appunti dal più
 * vecchio e quanti sono, se chi guarda la può ascoltare (`canListen`: una chiamata della telefonia, l'autore oppure chi ha
 * «Ascoltare le registrazioni dei colleghi», mai il contesto di piattaforma, e un Lead nel perimetro di lettura di chi guarda,
 * come `GET /api/calls/:callId/recording`), se è stata inserita a mano (`manual`) o viene da Close (`fromClose`), e se conta
 * (`valid`: falso per una chiamata mai arrivata al Lead per un errore tecnico, anche importata da Close, che la Timeline dice «Non
 * riuscita, non conta» senza la riga della registrazione; aggiunto con la revisione del Task 19).
 */
export const conversationRowSchema = z.object({
    call: callViewSchema,
    valid: z.boolean(),
    lead: conversationLeadSchema.nullable(),
    ownerAtCall: actorViewSchema.nullable(),
    ownerChanged: z.boolean(),
    recording: timelineRecordingSchema.nullable(),
    notesCount: z.number().int(),
    notes: z.array(conversationNoteSchema),
    canListen: z.boolean(),
    manual: z.boolean(),
    fromClose: z.boolean(),
});
/** Una pagina; `nextCursor` nullo quando non ce ne sono altre; `canFilterByPerson`: si sceglie «Di chi» (`who`). */
/**
 * Le persone delle scelte «Di chi» e «Owner allora» (`GET /api/conversations/people`): tutte le appartenenze dell'azienda, anche
 * quelle che non possono avere Lead (un Admin che chiama), sospese o archiviate, perché le loro telefonate restano in «Conversazioni».
 */
export const conversationPeopleResponseSchema = z.object({ people: z.array(z.object({ membershipId: z.string(), displayName: z.string() })) });
export const conversationsPageSchema = z.object({
    rows: z.array(conversationRowSchema),
    nextCursor: z.string().nullable(),
    canFilterByPerson: z.boolean(),
});
// ---------------------------------------------------------------------------------------------------------------
// «Conversazioni → WhatsApp» (piano M6, R3, passo 12a; D3, D11, D24; questioni 357, 388, 418, 419 e 425; ruling R3 «Archiviati,
// uniti, ricerca»): `GET /api/conversations/whatsapp`, i WhatsApp nel perimetro dell'area `conversations` di chi guarda, con la
// stessa regola delle telefonate e la colonna dei WhatsApp. «Della persona» (questioni 388 e 418) vuol dire: i WhatsApp dei Lead di
// cui è owner, quelli che ha scritto lei e quelli in arrivo che le appartengono (la persona del numero dell'azienda che li ha
// ricevuti); con «tutti» tutti; nella «Guarda come…» quelli della persona osservata. Un messaggio da un numero che non è in
// Gioia365 (questione 357) è senza Lead: lo vedono la persona del numero e chi vede tutti. I messaggi di un Lead archiviato escono
// con lui (tornano quando lo si ripristina), quelli di un Lead unito sono già del Lead che resta. Nessuna copia: ogni riga è il
// messaggio di `whatsapp_messages`, lo stesso della Timeline del Lead. La lettura («Segna come letto», «Segna tutti come letti» con
// gli stessi filtri, il numero da leggere: passo 11c, in fondo al file) usa le stesse condizioni.
/**
 * I filtri della scheda «WhatsApp», con gli stessi nomi e le stesse regole per la lista e per «Segna tutti come letti» (in fondo al
 * file): il periodo come le telefonate (in giorni interi nel fuso dell'azienda), «Di chi» (`who`: soltanto per chi vede tutti, i
 * WhatsApp di quella persona con la regola «della persona»), «Da leggere» (`unread`: i messaggi in arrivo non ancora letti; `true` o
 * `false`, alla lettera), la ricerca nel testo del Lead (nomi, salone, email, luogo, cifre dei telefoni) e, per un numero che non è in
 * Gioia365, nelle cifre del numero; il testo dei messaggi non si cerca, come nel mock-up.
 */
const whatsAppConversationFilters = {
    period: z.enum(CONVERSATION_PERIODS).default("last7"),
    from: isoDaySchema.optional(),
    to: isoDaySchema.optional(),
    who: idSchema.optional(),
    unread: z.stringbool({ truthy: ["true"], falsy: ["false"], case: "sensitive" }).optional(),
    q: freeTextSchema(200).optional(),
};
/**
 * Query di `GET /api/conversations/whatsapp`: i filtri della scheda (sopra), il cursore e il limite. Un parametro sconosciuto, un
 * valore fuori dai limiti o un cursore che non viene da una pagina è `422 validation_failed` con il nome del parametro.
 */
export const whatsAppConversationsQuerySchema = z
    .strictObject({
    ...whatsAppConversationFilters,
    cursor: z.string().max(CONVERSATIONS_CURSOR_MAX_LENGTH).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
})
    .superRefine(checkPeriod);
/**
 * Il messaggio della riga: la direzione (`in` il Lead ha scritto, `out` è partito dall'azienda), il canale, lo stato (D3), l'ora
 * (del fornitore per un messaggio in arrivo), il testo come è partito o arrivato (per un messaggio in arrivo che non è un testo la
 * didascalia, e `contentKind` dice che cosa ha mandato il Lead: questione 425), il modello da cui nasce con il suo nome di adesso
 * (`null` per un testo libero, una risposta, e per un messaggio in arrivo), il codice di un messaggio non riuscito, per un
 * messaggio in arrivo quando è stato letto e da chi («Letto da …», questione 423; tutti e due `null` finché è «Da leggere»; `readAt`
 * senza `readBy` è una lettura del contesto di Gioia365, «Letto da Gioia365 il …»), e `automatic` per
 * un messaggio che Gioia365 manda da sé, senza autore né persona (la conferma dello «STOP», questione 420: «Gioia365
 * (automatico)»).
 */
export const whatsAppConversationMessageSchema = z.object({
    id: z.string(),
    direction: z.enum(["in", "out"]),
    channel: whatsAppChannelSchema,
    status: whatsAppMessageStatusSchema,
    at: z.string(),
    body: z.string(),
    contentKind: whatsAppContentKindSchema.nullable(),
    template: z.object({ key: whatsAppTemplateKeySchema, name: z.string() }).nullable(),
    failureCode: z.string().nullable(),
    readAt: z.string().nullable(),
    readBy: z.string().nullable(),
    automatic: whatsAppAutomaticMessageSchema.nullable(),
});
/**
 * Il numero dell'azienda da cui parte «Rispondi su WhatsApp» (questione 419): quello che ha ricevuto il messaggio del Lead, come lo
 * ha oggi Gioia365 (il WhatsApp di una persona, con il modo, se è pronto e se l'azienda lo usa, come `whatsapp.sendVia` della
 * scheda), con la persona a cui appartiene («dal WhatsApp ufficiale di …»). `null` quando Gioia365 non ha più quel numero.
 */
export const whatsAppReplyViaSchema = leadWhatsAppSendViaSchema.extend({ person: personSchema });
/**
 * Ciò che serve a «Rispondi su WhatsApp» per un messaggio del Lead, prima del clic: fino a quando si risponde con parole proprie
 * (`windowEndsAt`: 24 ore dall'ultimo messaggio arrivato **a quel numero dell'azienda da quel telefono del Lead**, questione 419: Meta
 * conta la finestra fra il numero dell'azienda e l'utente; dopo, soltanto un modello approvato) e il numero da cui parte la risposta
 * (`via`). La risposta va al telefono da cui il Lead ha scritto (`leadE164`). La rotta della risposta ricontrolla tutto al clic.
 */
export const whatsAppConversationReplySchema = z.object({ windowEndsAt: z.string(), via: whatsAppReplyViaSchema.nullable() });
/**
 * Una riga di «Conversazioni → WhatsApp»:
 * - `message`: il messaggio (`whatsAppConversationMessageSchema`);
 * - `lead`: il Lead, come nelle telefonate; `null` per un numero che non è in Gioia365 («Numero non in Gioia365», questione 357);
 * - `leadE164`: il numero del Lead del messaggio (chi ha scritto, o a chi è partito); `null` per chi scrive con il solo nome
 *   utente; `companyE164`: il numero dell'azienda (che ha ricevuto, o da cui è partito; `null` per un messaggio a mano di chi non
 *   ha un WhatsApp impostato);
 * - `person`: «Di chi»: chi l'ha scritto, per un messaggio in uscita (nessuno per quelli che Gioia365 manda da sé, `automatic`), la
 *   persona del numero che l'ha ricevuto, per uno in arrivo (questione 418; nessuno se il numero non è di nessuno);
 * - `reply`: «Rispondi», soltanto su un messaggio del Lead (con il Lead) arrivato da un telefono che è ancora del Lead (non per chi
 *   scrive con il solo nome utente, né da un numero tolto al Lead), e per chi può rispondere (la funzione «Mandare WhatsApp», un Lead
 *   che può modificare, fuori dalla «Guarda come…» e non dal contesto di Gioia365); `null` altrimenti.
 */
export const whatsAppConversationRowSchema = z.object({
    message: whatsAppConversationMessageSchema,
    lead: conversationLeadSchema.nullable(),
    leadE164: z.string().nullable(),
    companyE164: z.string().nullable(),
    person: personSchema.nullable(),
    reply: whatsAppConversationReplySchema.nullable(),
});
/**
 * Una pagina, dal più recente; `nextCursor` nullo quando non ce ne sono altre; `canFilterByPerson`: si sceglie «Di chi» (`who`);
 * `readUntil`: l'ora del server con cui la pagina è stata letta (ISO), che «Segna tutti come letti» riceve come `until` dalla prima
 * pagina che si ha davanti.
 */
export const whatsAppConversationsPageSchema = z.object({
    rows: z.array(whatsAppConversationRowSchema),
    nextCursor: z.string().nullable(),
    canFilterByPerson: z.boolean(),
    readUntil: z.iso.datetime(),
});
// ---------------------------------------------------------------------------------------------------------------
// La lettura dei WhatsApp dei Lead (piano M6, R3, passo 11c; D24; questione 423; ruling R3 «Lettura»). Un messaggio del Lead è «Da
// leggere» finché qualcuno non lo legge in Gioia365: lo stato è del messaggio, uguale per tutti, e ricorda chi l'ha letto e quando
// («Letto da …», `readAt`/`readBy` della riga e della Timeline), una volta sola. Si segna letto nel perimetro di «Conversazioni» di chi
// legge, con la stessa regola «della persona» della lista (questioni 388 e 418); chi può leggere un messaggio in «Conversazioni» lo
// segna letto (decisione del controller sull'11c): anche il contesto di Gioia365 («Letto da Gioia365 il …», senza persona) e chi è
// nella «Guarda come…», nel perimetro della persona osservata e con il proprio nome, come le altre scritture del repository:
// - `POST /api/whatsapp/messages/:messageId/read`: un messaggio del Lead che chi legge vede in «Conversazioni»; `204`, anche quando era
//   già letto (resta chi l'ha letto prima); un messaggio che non c'è, partito dall'azienda o fuori dal perimetro è `404 not_found`;
// - `POST /api/whatsapp/messages/read-all` («Segna tutti come letti»): quelli da leggere che si vedono con i filtri della scheda
//   (questione 423), con la query qui sotto; `204`;
// - `GET /api/whatsapp/unread-count`: il numero da leggere accanto a «Conversazioni» e sul pulsante della scheda «WhatsApp» (D24), nel
//   perimetro di chi guarda (nella «Guarda come…» quello della persona osservata), di tutti i giorni.
/**
 * Query di `POST /api/whatsapp/messages/read-all`: i filtri della lista (`whatsAppConversationsQuerySchema` senza il cursore e il
 * limite: gli stessi nomi, gli stessi valori di partenza, le stesse regole; «Da leggere» non cambia niente, perché si segnano soltanto
 * quelli da leggere) e `until`, il `readUntil` della prima pagina della lista che chi legge ha davanti: si segnano soltanto i messaggi
 * arrivati in Gioia365 fino a quell'ora, non secondo l'ora di Meta (`at`), così uno arrivato dopo, anche se il Lead l'aveva scritto
 * prima e Meta l'ha consegnato in ritardo, resta da leggere. Un parametro sconosciuto o sbagliato è `422 validation_failed` con il suo
 * nome.
 */
export const readAllWhatsAppQuerySchema = z.strictObject({ ...whatsAppConversationFilters, until: z.iso.datetime().optional() }).superRefine(checkPeriod);
/** Risposta di `GET /api/whatsapp/unread-count`: quanti messaggi del Lead sono da leggere nel perimetro di chi guarda. */
export const whatsAppUnreadCountSchema = z.object({ count: z.number().int().min(0) });
// ---------------------------------------------------------------------------------------------------------------
// «Conversazioni → Email» (piano dell'ingresso del 12 ottobre 2026, I5, D5; INGR-22, RUOLI-16): `GET /api/conversations/email`, gli
// eventi delle email di GoHighLevel dei Lead nel perimetro dell'area `conversations` di chi guarda, in sola lettura, dal più recente.
// «Della persona» vuol dire: gli eventi dei Lead di cui è owner (le email le manda GoHighLevel, non una persona); con «tutti» tutti;
// nella «Guarda come…» quelli della persona osservata. Un evento senza Lead non si vede; quelli di un Lead archiviato escono con lui,
// quelli di un Lead unito sono già del Lead che resta. Nessuna copia: ogni riga è l'evento di `email_events`, lo stesso della Timeline.
/**
 * Query di `GET /api/conversations/email`: il periodo come le altre schede (sull'ora dell'evento), «Di chi» (`who`: soltanto per chi
 * vede tutti, i Lead di cui quella persona è owner), la ricerca nel testo del Lead, il cursore e il limite. Un parametro sconosciuto,
 * un valore fuori dai limiti o un cursore che non viene da una pagina è `422 validation_failed` con il nome del parametro.
 */
export const emailConversationsQuerySchema = z
    .strictObject({
    period: z.enum(CONVERSATION_PERIODS).default("last7"),
    from: isoDaySchema.optional(),
    to: isoDaySchema.optional(),
    who: idSchema.optional(),
    q: freeTextSchema(200).optional(),
    cursor: z.string().max(CONVERSATIONS_CURSOR_MAX_LENGTH).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
})
    .superRefine(checkPeriod);
/** Una riga: l'evento (che cosa, quando, l'oggetto e il link se il flusso li manda) e il suo Lead, come nelle altre schede. */
export const emailConversationRowSchema = z.object({
    event: z.object({ id: z.string(), type: emailEventSchema, at: z.string(), subject: z.string().nullable(), url: z.string().nullable() }),
    lead: conversationLeadSchema,
});
/** Una pagina, dal più recente; `nextCursor` nullo quando non ce ne sono altre; `canFilterByPerson`: si sceglie «Di chi» (`who`). */
export const emailConversationsPageSchema = z.object({
    rows: z.array(emailConversationRowSchema),
    nextCursor: z.string().nullable(),
    canFilterByPerson: z.boolean(),
});
//# sourceMappingURL=conversations.js.map